import { useSearchParams } from "#routers/hooks";
import {
  FacilityTablePanel,
  RecordsListTablePanel,
} from "#src/batteries-included-components";
import { ApplyAutomationProgressBanner } from "#src/batteries-included-components/Banners/ApplyAutomationProgressBanner";
import { TemplatedConfigurationRunStatusWithQueue } from "#src/batteries-included-components/Banners/TemplatedConfigurationRunStatus";
import { CreateAssetDropdownMenu } from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu";
import {
  FacilityRecordsTableFilterArea,
  FacilityRecordsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/RecordsFilterAreas";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  generateDocumentTitle,
  Page,
  Tab,
} from "@validereinc/common-components";
import { AssetType, ResourceDefinitions, Resources } from "@validereinc/domain";
import React from "react";
import { Titled } from "react-titled";
import { FacilitiesListRoute } from "./";

const DEFAULT_TAB_KEY = "list";

export const FacilityListPage = () => {
  const { localize, isLoading } = useLocalization();
  const [breadcrumbs] = useBreadcrumbsFromRoute(FacilitiesListRoute);
  const [searchParams, setSearchParams] = useSearchParams();
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [calcsAndRecordsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "calculations-records-shared"
  );
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["facilities", "list"]
  );
  const [
    recordsFilterConfigStorageKey,
    recordsTableConfigStorageKey,
    recordsTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "facilities"]
  );
  const [recordsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "facilities", "apply-automation"]
  );
  const [configsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    "facilities-configs-apply-automation"
  );

  const actionRow = <CreateAssetDropdownMenu assetType={AssetType.FACILITY} />;
  const pageTitle = localize(FacilitiesListRoute.title);

  return (
    <Page
      breadcrumbs={breadcrumbs}
      category={FacilitiesListRoute.subtitle}
      title={pageTitle}
      actionRow={actionRow}
      isLoading={isLoading}
      activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
      onActiveTabKeyChange={(tabKey: string) => {
        setSearchParams({ tab: tabKey });
      }}
    >
      <Tab
        title="List"
        tabKey="list"
      >
        <TemplatedConfigurationRunStatusWithQueue
          primaryResourceType={Resources.FACILITY}
        />
        <FacilityTablePanel
          filterConfigStorageKey={filterConfigStorageKey}
          tableConfigStorageKey={tableConfigStorageKey}
          viewConfigStorageKey={assetsSharedViewConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
          groupByTablesStateStorageKey={groupByTablesStateStorageKey}
        />
      </Tab>
      <Tab
        title="Records"
        tabKey="records"
      >
        <Titled
          title={generateDocumentTitle(
            `${localize(Resources.FACILITY)} ${ResourceDefinitions.record.label.plural}`
          )}
        >
          <ApplyAutomationProgressBanner
            jobStorageKey={recordsApplyAutomationJobStorageKey}
            showCounts={true}
          />
          <ApplyAutomationProgressBanner
            jobStorageKey={configsApplyAutomationJobStorageKey}
            showCounts={false}
          />
          <FacilityRecordsViewFilterArea
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
          />
          <RecordsListTablePanel
            type={AssetType.FACILITY}
            filterConfigStorageKey={recordsFilterConfigStorageKey}
            tableConfigStorageKey={recordsTableConfigStorageKey}
            tableStateStorageKey={recordsTableStateStorageKey}
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            applyAutomationJobStorageKey={recordsApplyAutomationJobStorageKey}
            applySeededAutomationJobStorageKey={
              configsApplyAutomationJobStorageKey
            }
            filterComponentSlot={({ recordValueTypes }) => (
              <FacilityRecordsTableFilterArea
                filterConfigStorageKey={recordsFilterConfigStorageKey}
                selectableProperties={recordValueTypes}
              />
            )}
          />
        </Titled>
      </Tab>
    </Page>
  );
};
